import { memo, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Box, Button, FormControl, MenuItem, Select, Typography } from '@mui/material'
import PropTypes from 'prop-types'

import {
    useGetOrderItemsQuery,
    useLazyGetOrderItemDrawingQuery,
    useUpdateOrderItemDrawingMutation,
    useUpdateOrderItemMutation,
} from '@/app/services/orderItems'
import { useCalculateOrderMutation, useGetOrderQuery } from '@/app/services/orders'
import {
    addItemToUpdateQueue,
    removeItemFromUpdateQueue,
    setOpenDrawingDoctorModal,
    setSelectedOrderItemDrawingId,
    setSelectedOrderStatus,
} from '@/app/slices/orderItemsSlice'
import { selectOrganisationId, selectWebStoreDrawingDoctorEnabled } from '@/app/slices/organisationSlice'
import RenderPartSize from '@/common/components/RenderPartSize/RenderPartSize'
import TbxTooltip from '@/common/components/TbxTooltip/TbxTooltip'
import ThumbnailImage from '@/common/components/ThumbnailImage/ThumbnailImage'
import { useMutationErrorSnackbar } from '@/common/hooks/useMutationErrorSnackbar'
import { MATERIAL_PROFILE_TYPES, ORDER_STATUS_ARRAY } from '@/common/utils'

const classes = {
    ItemDetails_Image: {
        minWidth: { xs: '140px', xl: '220px' },
        maxWidth: '260px',
        gap: '8px',
    },

    ItemDetails_Options_Size: {
        gap: '12px',
    },
    ItemDetails_TextButton: {
        textTransform: 'none',
        fontWeight: 'normal',
        color: 'text.primary',
        fontSize: '14px',
        textDecoration: 'underline',
        textDecorationThickness: '1px',
    },
}

const ItemDrawing = ({ orderItemId }) => {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const { showMutationError } = useMutationErrorSnackbar()
    const { orderId } = useParams()

    const organisationId = useSelector(selectOrganisationId)
    const drawingDoctorEnabled = useSelector(selectWebStoreDrawingDoctorEnabled)

    const { data: selectedOrder, isFetching: isFetchingOrder } = useGetOrderQuery({
        organisationId,
        orderId,
    })

    const { orderItem } = useGetOrderItemsQuery(
        {
            organisationId,
            orderId,
        },
        {
            selectFromResult: ({ data }) => ({
                orderItem: data?.find((item) => item.id === orderItemId),
            }),
        }
    )

    const [getOrderItemDrawing] = useLazyGetOrderItemDrawingQuery()
    const [updateOrderItem, { isLoading: isUpdating }] = useUpdateOrderItemMutation()

    const [updateOrderItemDrawing] = useUpdateOrderItemDrawingMutation()

    const [, { isLoading: isCalculating }] = useCalculateOrderMutation({
        fixedCacheKey: 'shared-calculate-order',
    })

    const [itemUnit, setItemUnit] = useState(orderItem?.isImperial ? 'in' : 'mm')

    const isPartFromLibrary = Boolean(orderItem?.partLibraryEntryId)

    const handleUpdateOrderItemDrawing = async (orderItemDrawing) => {
        orderItemDrawing.paths = Object.values(orderItemDrawing.paths)
        const drawing = { ...orderItemDrawing }

        const result = await updateOrderItemDrawing({
            organisationId,
            orderId,
            orderItemId,
            drawing,
        }).unwrap()
        return result
    }

    const renderPartSize = (part) => {
        const isRotary = 'profile' in part && Boolean(part?.profile)
        const hasFlatDims = Boolean(part?.displayBoundBoxWidth && part?.displayBoundBoxHeight)
        if (!isRotary && !hasFlatDims) return null
        return (
            <RenderPartSize
                part={part}
                variant="strong2"
            />
        )
    }

    const handleUpdateOrderItem = async (orderItem) => {
        try {
            await updateOrderItem({
                organisationId,
                orderId,
                orderItemId: orderItem?.id,
                orderItem,
            }).unwrap()
        } catch (error) {
            showMutationError(error, t('Failed to update the order item'))
        }
    }

    const handleThumbnailClick = useCallback(() => {
        dispatch(setSelectedOrderItemDrawingId(orderItemId))
        dispatch(setOpenDrawingDoctorModal(true))
    }, [dispatch, orderItemId])

    const handleUnitChange = async (event) => {
        dispatch(addItemToUpdateQueue(orderItemId))
        // Backend sets the order to NotCalculated as part of the item update (and updateOrderItem
        // refetches the order), so we only reflect it optimistically here — no racing updateOrder PUT.
        if (selectedOrder.status !== ORDER_STATUS_ARRAY.NotCalculated) {
            dispatch(setSelectedOrderStatus(ORDER_STATUS_ARRAY.NotCalculated))
        }
        const value = event?.target?.value
        if (Boolean(value) && itemUnit !== value) {
            setItemUnit(value)
            const drawing = await getOrderItemDrawing(
                {
                    organisationId,
                    orderId,
                    orderItemId,
                },
                true
            ).unwrap()

            await handleUpdateOrderItem({
                ...orderItem,
                isImperial: value === 'in',
            })
            await handleUpdateOrderItemDrawing({
                ...drawing,
                isImperial: value === 'in',
            })

            dispatch(removeItemFromUpdateQueue(orderItemId))
        }
    }

    return (
        <Box
            sx={[
                {
                    alignItems: 'center',
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'column',
                    justifyContent: 'space-around',
                },
                classes.ItemDetails_Image,
            ]}
        >
            {orderItem?.partId ? (
                <Typography>
                    {t('Part ID')}: {orderItem?.partId}
                </Typography>
            ) : null}
            <ThumbnailImage
                data-testid="order-item-thumbnail"
                key={orderItemId}
                uri={orderItem?.thumbnailUri}
                onClick={handleThumbnailClick}
            />
            <Button
                data-testid="order-item-open-drawing-doctor"
                sx={classes.ItemDetails_TextButton}
                variant="text"
                onClick={handleThumbnailClick}
            >
                {drawingDoctorEnabled ? t('Open Drawing Doctor') : t('View part')}
            </Button>
            {'profile' in orderItem ? (
                <Typography variant="strong1">
                    {t('Profile')}: {t(MATERIAL_PROFILE_TYPES[orderItem?.profile]?.label ?? orderItem?.profile)}
                </Typography>
            ) : null}
            <Box
                sx={[
                    {
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                    },
                    classes.ItemDetails_Options_Size,
                ]}
            >
                {renderPartSize(orderItem)}
                <TbxTooltip
                    title={isPartFromLibrary ? t('Cannot edit parts from library') : ''}
                    arrow
                >
                    <FormControl
                        color="primary"
                        disabled={isUpdating}
                        margin="none"
                        size="small"
                        sx={classes.formControl_small}
                        variant="outlined"
                    >
                        <Select
                            data-testid="order-item-select-unit"
                            disabled={isUpdating || isCalculating || isFetchingOrder || isPartFromLibrary}
                            id="select-itemUnit"
                            labelId="select-itemUnit-label"
                            value={itemUnit}
                            onChange={handleUnitChange}
                        >
                            <MenuItem value="mm">{t('Millimetres')}</MenuItem>
                            <MenuItem value="in">{t('Inches')}</MenuItem>
                        </Select>
                    </FormControl>
                </TbxTooltip>
            </Box>
        </Box>
    )
}

ItemDrawing.propTypes = {
    orderItemId: PropTypes.string,
}

export default memo(ItemDrawing)
